import { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "./supabase";
import { apiGet } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [schools, setSchools] = useState([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState(null);

  // Fetch profile from backend (same pattern as recruiter dashboard).
  // If /auth/me returns 401/403 (session is stale or revoked) even after
  // api.js has already attempted a token refresh, the only safe move is to
  // sign the user out so LoginPage renders again. Without this, App.jsx's
  // "user && !profile" branch traps the user on the Loading profile spinner.
  const fetchProfile = async (accessToken) => {
    try {
      const data = await apiGet("/auth/me", accessToken);
      if (data.profile) {
        setProfile(data.profile);
      } else {
        setProfile(null);
      }
    } catch (err) {
      console.error("Failed to fetch profile:", err);
      if (/API 40[13]\b/.test(err?.message || "")) {
        // Stale/revoked session — bail out cleanly to the login screen.
        await supabase.auth.signOut();
      } else {
        setProfile(null);
      }
    }
  };

  const refreshProfile = async () => {
    if (session?.access_token) {
      await fetchProfile(session.access_token);
    }
  };

  // Fetch schools list for platform admins
  const fetchSchools = async (accessToken) => {
    try {
      const data = await apiGet("/schools", accessToken);
      setSchools(data.schools || []);
    } catch (err) {
      console.error("Failed to fetch schools:", err);
      setSchools([]);
    }
  };

  useEffect(() => {
    // Demo OTP auto-login: verify OTP first, then check session
    const params = new URLSearchParams(window.location.search);
    const demoOtp = params.get("demo_otp");
    const demoEmail = params.get("demo_email");

    const initSession = async () => {
      if (demoOtp && demoEmail) {
        // Clean URL immediately so OTP doesn't linger
        params.delete("demo_otp");
        params.delete("demo_email");
        const clean = params.toString();
        window.history.replaceState({}, "", clean ? `?${clean}` : window.location.pathname);

        const { error } = await supabase.auth.verifyOtp({ email: demoEmail, token: demoOtp, type: "magiclink" });
        if (error) console.error("Demo OTP verify failed:", error);
        // After verifyOtp, getSession below will have the new session
      }

      const { data: { session: s } } = await supabase.auth.getSession();
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.access_token) {
        await fetchProfile(s.access_token);
        fetchSchools(s.access_token);
      }
      setLoading(false);
    };

    initSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, s) => {
        setSession(s);
        setUser(s?.user ?? null);
        if (s?.access_token) {
          await fetchProfile(s.access_token);
          await fetchSchools(s.access_token);
        } else {
          setProfile(null);
          setSchools([]);
          setSelectedSchoolId(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const isPlatformAdmin = useMemo(() => !profile?.school_id, [profile?.school_id]);

  // School ID: for platform admins, use selected school or URL param; for careers admins, use profile value
  const schoolId = useMemo(() => {
    const urlParam = new URLSearchParams(window.location.search).get("school_id");
    if (urlParam) return urlParam;
    if (isPlatformAdmin) return selectedSchoolId;
    return profile?.school_id || null;
  }, [profile?.school_id, isPlatformAdmin, selectedSchoolId]);

  const switchSchool = useCallback((id) => {
    setSelectedSchoolId(id);
  }, []);

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signUp = async (email, password) => {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setSchools([]);
    setSelectedSchoolId(null);
  };

  const resetPassword = async (email) => {
    const redirectTo = `${window.location.origin}${window.location.pathname}`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    if (error) throw error;
  };

  // Passwordless email sign-in. Only works for existing accounts —
  // shouldCreateUser:false prevents the magic-link flow from minting new users.
  const sendMagicLink = async (email) => {
    const emailRedirectTo = `${window.location.origin}${window.location.pathname}`;
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo, shouldCreateUser: false },
    });
    if (error) throw error;
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      profile,
      loading,
      schoolId,
      schools,
      isPlatformAdmin,
      switchSchool,
      signIn,
      signUp,
      signOut,
      refreshProfile,
      resetPassword,
      sendMagicLink,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
